import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Grid,
    CircularProgress,
    Button,
    Link,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { useInView } from "react-intersection-observer";
import ProductCard from "../components/ProductCard/ProductCard";
import { useSearchParams, useLocation } from "react-router-dom";
import { useProductCategoryContext } from "../contexts/ProductCategoryContext";

interface ProductListingPageProps {
    mode: "all-products" | "category" | "search";
    searchQuery?: string;
}

const ProductListingPage: React.FC<ProductListingPageProps> = ({ mode, searchQuery }) => {
    const { sku } = useParams<{ sku: string }>();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [searchParams] = useSearchParams();
    const location = useLocation();

    useEffect(() => {
        if (!searchParams.get("mpage")) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set("mpage", "1");

            navigate(
                {
                    pathname: location.pathname,
                    search: newParams.toString(),
                },
                { replace: true },
            );
        }
    }, [searchParams, navigate, location.pathname]);

    const [ref, inView] = useInView({
        threshold: 0,
        rootMargin: "100px",
    });

    const {
        category,
        products,
        loading,
        loadingMore,
        error,
        breadcrumbs,
        pagination,
        loadCategoryData,
        loadAllProductsData,
        loadSearchResults,
        loadMoreProducts,
        saveListingCache,
        restoreListingCache,
        getCacheKey,
    } = useProductCategoryContext();

    const preloadPages = Number(searchParams.get("mpage")) || 1;

    const loadData = async () => {
        const cacheKey =
            mode === "category"
                ? `category:${sku}`
                : mode === "search"
                  ? `search:${searchQuery}`
                  : "products";

        // SI hay cache ? usamos eso y NO cargamos nada
        //if (restoreListingCache(cacheKey)) {
        //    return;
        //}

        // si no hay cache ? fetch normal
        if (mode === "all-products") {
            await loadAllProductsData();
        } else if (mode === "category" && sku) {
            await loadCategoryData(sku, { pagina: 1, tamanoPagina: 20 });
        } else if (mode === "search" && searchQuery) {
            await loadSearchResults(searchQuery);
        }
    };

    useEffect(() => {
        loadData();
    }, [mode, sku, searchQuery]);

    useEffect(() => {
        if (inView && !loading && !loadingMore && pagination.actualPage < pagination.cantPages) {
            loadMoreProducts();
        }
    }, [
        inView,
        loading,
        loadingMore,
        pagination.actualPage,
        pagination.cantPages,
        loadMoreProducts,
    ]);

    const handleProductClick = (productSku: string) => {
        const cacheKey =
            mode === "category"
                ? `category:${sku}`
                : mode === "search"
                  ? `search:${searchQuery}`
                  : "products";

        saveListingCache(cacheKey, window.scrollY);

        navigate(`/productos/${productSku}`);
    };

    const renderBreadcrumbs = () => {
        if (mode === "search") {
            return null;
        }

        if (mode === "all-products") {
            return (
                <Typography
                    sx={{
                        fontFamily: "'Rubik', sans-serif",
                        fontSize: "12px",
                        fontWeight: 300,
                        color: "#010101",
                        textAlign: "center",
                        wordSpacing: 4,
                    }}
                >
                    <Link
                        component="button"
                        onClick={() => navigate("/")}
                        color="inherit"
                        sx={{
                            textDecoration: "none",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            transition: "color 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&:hover": { color: "#9e9e9e" },
                        }}
                    >
                        Inicio
                    </Link>
                    {" > "}

                    <Typography
                        component="span"
                        color="text.primary"
                        sx={{
                            fontFamily: "'Rubik', sans-serif",
                            fontSize: "12px",
                            fontWeight: 300,
                            color: "#010101",
                            textAlign: "center",
                        }}
                    >
                        Productos
                    </Typography>
                </Typography>
            );
        } else {
            return (
                <Box sx={{ textAlign: "center", mb: 2 }}>
                    <Typography
                        component="span"
                        sx={{
                            fontFamily: "'Rubik', sans-serif",
                            fontSize: "12px",
                            fontWeight: 300,
                            color: "#010101",
                            textTransform: "uppercase",
                        }}
                    >
                        <Link
                            component="button"
                            onClick={() => navigate("/")}
                            color="inherit"
                            sx={{
                                textDecoration: "none",
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                transition: "color 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                "&:hover": { color: "#9e9e9e" },
                            }}
                        >
                            Inicio
                        </Link>
                        {" > "}
                        {breadcrumbs.map((crumb, index) => (
                            <React.Fragment key={index}>
                                <Link
                                    component="button"
                                    onClick={() =>
                                        index < breadcrumbs.length - 1 && navigate(crumb.href)
                                    }
                                    color={
                                        index === breadcrumbs.length - 1
                                            ? "text.primary"
                                            : "inherit"
                                    }
                                    sx={{
                                        textDecoration: "none",
                                        background: "none",
                                        border: "none",
                                        cursor:
                                            index === breadcrumbs.length - 1
                                                ? "default"
                                                : "pointer",
                                        transition: "color 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                        "&:hover": {
                                            color:
                                                index === breadcrumbs.length - 1
                                                    ? "text.primary"
                                                    : "#9e9e9e",
                                        },
                                    }}
                                >
                                    {crumb.label}
                                </Link>
                                {index < breadcrumbs.length - 1 && " > "}
                            </React.Fragment>
                        ))}
                    </Typography>
                </Box>
            );
        }
    };

    const getPageTitle = () => {
        if (mode === "all-products") {
            return "Productos";
        } else if (mode === "search") {
            return "Resultados de búsqueda";
        } else {
            return category?.nombre || (sku ? sku.replace(/-/g, " ").toUpperCase() : "");
        }
    };

    const getNoProductsMessage = () => {
        if (mode === "all-products") {
            return "No se encontraron productos";
        } else if (mode === "search") {
            return `No se encontraron resultados para "${searchQuery}"`;
        } else {
            return "No se encontraron productos en esta categoría";
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                    {mode === "all-products"
                        ? "Cargando productos..."
                        : mode === "category"
                          ? "Cargando categoría..."
                          : "Buscando productos..."}
                </Typography>
            </Box>
        );
    }

    if (error && products.length === 0) {
        return (
            <Box sx={{ py: 4, textAlign: "center", px: 2 }}>
                <Typography variant="h4" color="error" gutterBottom>
                    {error}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    {mode === "all-products"
                        ? "No pudimos cargar los productos."
                        : mode === "category"
                          ? "No pudimos encontrar la categoría solicitada."
                          : "No pudimos completar la búsqueda."}
                </Typography>
                <Button variant="contained" onClick={loadData}>
                    Reintentar
                </Button>
            </Box>
        );
    }

    if (mode === "search" && !searchQuery) {
        return (
            <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography variant="h4" gutterBottom>
                    Búsqueda vacía
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Por favor, ingresa un término de búsqueda.
                </Typography>
            </Box>
        );
    }

    return (
        <>
            {renderBreadcrumbs()}

            <Box textAlign="center" sx={{ mb: 6 }}>
                <Typography
                    variant="h1"
                    sx={{
                        fontFamily: "'Rubik', sans-serif",
                        fontSize: isMobile ? "40px" : "60px",
                        fontWeight: "bold",
                        color: "#010101",
                        lineHeight: 1.2,
                    }}
                >
                    {getPageTitle()}
                </Typography>
            </Box>

            <Grid container spacing={0}>
                {products.map((product) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={product.id} sx={{ display: "flex" }}>
                        <ProductCard
                            product={product}
                            variant="showcase-small"
                            onClick={() => handleProductClick(product.sku)}
                        />
                    </Grid>
                ))}
            </Grid>

            {(loadingMore || pagination.actualPage < pagination.cantPages) && (
                <Box ref={ref} display="flex" justifyContent="center" sx={{ mt: 4, py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ ml: 2 }}>
                        Cargando más productos...
                    </Typography>
                </Box>
            )}

            {error && products.length > 0 && (
                <Box textAlign="center" sx={{ mt: 4, py: 4 }}>
                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                        Error al cargar más productos
                    </Typography>
                    <Button variant="outlined" onClick={loadMoreProducts}>
                        Reintentar
                    </Button>
                </Box>
            )}

            {!loading && products.length === 0 && !error && (
                <Box textAlign="center" sx={{ mt: 8 }}>
                    <Typography variant="h6" color="text.secondary">
                        {getNoProductsMessage()}
                    </Typography>
                </Box>
            )}
        </>
    );
};

export default ProductListingPage;
